import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import LocationPrompt from './LocationPrompt';
import { IconButton, Button, Typography, Chip } from '@mui/material';
import { ReactComponent as EmptyCartSVG } from '../assets/img/Emptycart.svg';
import { Add, Remove, Close, InfoOutlined, LocalHospital, Warning } from '@mui/icons-material';
import { addToCart, removeFromCart, switchHealthcareVendor } from '../redux/healthcareCartSlice';
import { formatCurrency } from '../config/market';
import useMinimumOrderValue from "../hooks/useMinimumOrderValue";
import { StruckPrice, VendorOfferLine } from './PriceCut';
import { lineHasDeal, cartOfferSavings } from '../utils/vendorDiscounts';
import {
  linesSubtotal,
  cartMinimumVerdict,
  minimumOrderMessage,
} from '../utils/minimumOrder';
import MinimumOrderNotice from './MinimumOrderNotice';
import useCoverage from '../redux/useCoverage';
import { cartOutletId } from '../utils/vendorOutlets';
import BranchCartCheck from './branch/BranchCartCheck';
import CoverageBanner from './CoverageBanner';
import CartSummary from './CartSummary';
import useDeliveryEstimate from '../hooks/useDeliveryEstimate';
import { CartPromiseNote } from './LusukuPromise';
import './RestaurantCart.css'; // Reuse the restaurant cart styles

const HealthcareCart = ({ onClose, vendorId, isVisible }) => {
  const dispatch = useDispatch();
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const history = useHistory();
  const currentVendor = useSelector((state) => state.healthcareCart.currentHealthcareVendor);
  const cart = useSelector((state) => state.healthcareCart.carts[currentVendor] || {});
  const vendor = useSelector((state) => state.healthcareCart.vendor);

  // Pre-checkout coverage gate (backend /api/coverage — same decision as the
  // order-create gate) so the customer can't proceed to an undeliverable order.
  // A pharmacy branch (4 Oct 2026) is checked against ITS coordinates and
  // radius, read off the cart's lines like the restaurant cart does.
  const outletId = cartOutletId(cart);
  const coverage = useCoverage({ vendorId: currentVendor || vendorId, outletId, vendorType: 'healthcare' });

  const cartItems = Object.values(cart);

  /* Minimum order value, goods only. Summed from the lines rather than read off
     the slice total so this cart and the hub judge the same basket the same
     way. The server refuses a short basket at order-create; saying it here
     means the customer adds an item rather than meeting a 400 at the payment
     step. */
  const goodsSubtotal = linesSubtotal(cartItems);
  const minimumOrderValue = useMinimumOrderValue();
  /* The cart as the server prices it (POST /api/quote): the delivery fee, the
     total and the minimum-order verdict all come from this one answer, and the
     cart's own sum stands in only until it arrives. */
  const estimate = useDeliveryEstimate({
    vendorId: currentVendor || vendorId,
    outletId,
    items: cartItems,
    enabled: cartItems.length > 0,
  });
  const minimum = cartMinimumVerdict(estimate, { subtotal: goodsSubtotal, minimum: minimumOrderValue });
  const minimumShortfall = minimum.shortfall;

  const canProceed =
    cartItems.length > 0 &&
    minimumShortfall === 0 &&
    coverage.hasLocation &&
    coverage.isConfirmed;

  // Check if any items require prescription or have age restrictions
  const hasRestrictedItems = cartItems.some(
    (item) => item.requiresPrescription || item.ageRestriction > 0 || item.controlledSubstance
  );

  const handleIncrease = (product) => {
    const isProduct = product.productType === 'PRODUCT' || !product.productType;
    const atStockLimit = isProduct && product.countInStock > 0 && product.quantity >= product.countInStock;
    if (atStockLimit) return;
    dispatch(addToCart({ vendorId: currentVendor, product, quantity: 1, options: product.options }));
  };

  const handleDecrease = (product) => {
    dispatch(addToCart({ vendorId: currentVendor, product, quantity: -1, options: product.options }));
    if (product.quantity === 1) {
      dispatch(removeFromCart({ vendorId: currentVendor, productId: product._id }));
    }
  };

  const checkoutHandler = () => {
    if (!canProceed) return;
    dispatch(switchHealthcareVendor(currentVendor));
    history.push(`/healthcare/shipping/${vendorId}`);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          animation: 'fadeIn 0.3s ease'
        }}
        onClick={onClose}
      />
      
      {/* Cart Container */}
      <div 
        className="restaurant-cart-container" 
        style={{ 
          borderTop: '4px solid #1ee925ff',
          position: 'fixed',
          top: '50%',
          right: '20px',
          transform: 'translateY(-50%)',
          zIndex: 1001,
          animation: 'slideInRight 0.3s ease'
        }}
      >
        {/* Cart Header */}
      <div className="restaurant-cart-header">
        <h2 className="restaurant-cart-title" style={{ color: '#E91E63' }}>Your Healthcare Order</h2>
        {vendor && (
          <p className="restaurant-cart-subtitle">
            <LocalHospital className="restaurant-cart-subtitle-icon" style={{ color: '#E91E63' }} />
            {vendor.name}
          </p>
        )}
        <IconButton 
          className="restaurant-cart-close-btn"
          onClick={onClose}
          aria-label="close cart"
        >
          <Close />
        </IconButton>
      </div>

      {/* Cart Content */}
      <div className="restaurant-cart-content">
        {cartItems.length === 0 ? (
          <div className="restaurant-cart-empty">
            <div className="restaurant-cart-empty-icon-wrapper">
              <EmptyCartSVG className="restaurant-cart-empty-icon" />
            </div>
            <h3 className="restaurant-cart-empty-title">Your Cart is Empty</h3>
            <p className="restaurant-cart-empty-text">
              You haven't added any healthcare products or services yet. Browse our offerings and add them to your cart!
            </p>
          </div>
        ) : (
          <>
            {hasRestrictedItems && (
              <div style={{
                backgroundColor: '#fff3e0',
                border: '1px solid #ff9800',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Warning style={{ color: '#f57c00' }} fontSize="small" />
                <Typography variant="caption" style={{ color: '#f57c00', fontWeight: 500 }}>
                  This order contains restricted items that may require verification
                </Typography>
              </div>
            )}

            <ul className="restaurant-cart-items-list">
              {cartItems.map((item) => {
                const optionsTotal = item.options?.reduce((sum, option) => sum + option.price, 0) || 0;
                const itemTotal = (item.price + optionsTotal) * item.quantity;
                const listTotal = lineHasDeal(item)
                  ? (item.listPrice + optionsTotal) * item.quantity
                  : null;
                
                return (
                  <li key={item._id} className="restaurant-cart-item">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="restaurant-cart-item-image"
                    />
                    
                    <div className="restaurant-cart-item-details">
                      <div>
                        <h4 className="restaurant-cart-item-name">{item.name}</h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                          {item.requiresPrescription && (
                            <Chip 
                              label="℞ Rx" 
                              size="small" 
                              style={{ 
                                backgroundColor: '#ffebee', 
                                color: '#d32f2f', 
                                fontSize: '0.7rem',
                                height: '20px'
                              }} 
                            />
                          )}
                          {item.ageRestriction > 0 && (
                            <Chip 
                              label={`${item.ageRestriction}+`} 
                              size="small" 
                              style={{ 
                                backgroundColor: '#fff3e0', 
                                color: '#f57c00',
                                fontSize: '0.7rem',
                                height: '20px'
                              }} 
                            />
                          )}
                          {item.productType === 'SERVICE' && (
                            <Chip 
                              label="Service" 
                              size="small" 
                              style={{ 
                                backgroundColor: '#f3e5f5', 
                                color: '#E91E63',
                                fontSize: '0.7rem',
                                height: '20px'
                              }} 
                            />
                          )}
                        </div>
                      </div>
                      
                      {item.options && item.options.length > 0 && (
                        <div style={{ marginTop: '6px' }}>
                          {item.options.map((option, idx) => (
                            <Typography 
                              key={idx} 
                              variant="caption" 
                              style={{ 
                                display: 'block', 
                                color: '#666',
                                fontSize: '0.75rem'
                              }}
                            >
                              + {option.name} {option.price > 0 && `(+${option.price.toLocaleString()})`}
                            </Typography>
                          ))}
                        </div>
                      )}
                      
                      <p className="restaurant-cart-item-price">
                        {listTotal !== null && (
                          <StruckPrice>{formatCurrency(listTotal)}</StruckPrice>
                        )}
                        {formatCurrency(itemTotal)}
                      </p>
                      
                      <div className="restaurant-cart-item-actions">
                        <div className="restaurant-cart-quantity-controls">
                          <IconButton
                            className="restaurant-cart-quantity-btn decrease"
                            onClick={() => handleDecrease(item)}
                            size="small"
                            aria-label="decrease quantity"
                            style={{ borderColor: '#E91E63' }}
                          >
                            <Remove fontSize="small" style={{ color: '#E91E63' }} />
                          </IconButton>
                          <span className="restaurant-cart-quantity-display">
                            {item.quantity}
                          </span>
                          {(() => {
                            const isProduct = item.productType === 'PRODUCT' || !item.productType;
                            const atStockLimit = isProduct && item.countInStock > 0 && item.quantity >= item.countInStock;
                            return (
                              <>
                                <IconButton
                                  className="restaurant-cart-quantity-btn increase"
                                  onClick={() => handleIncrease(item)}
                                  size="small"
                                  aria-label="increase quantity"
                                  disabled={atStockLimit}
                                  title={atStockLimit ? `Only ${item.countInStock} in stock` : ''}
                                  style={{ borderColor: atStockLimit ? '#ccc' : '#E91E63' }}
                                >
                                  <Add fontSize="small" style={{ color: atStockLimit ? '#ccc' : '#E91E63' }} />
                                </IconButton>
                                {atStockLimit && (
                                  <span style={{
                                    fontSize: '0.65rem',
                                    color: '#d32f2f',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    marginLeft: '4px'
                                  }}>
                                    Max stock
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="restaurant-cart-summary">
              <CoverageBanner
                variant="inline"
                deliverable={coverage.deliverable}
                reason={coverage.reason}
                distanceKm={coverage.distanceKm}
                radius={coverage.radius}
                hasLocation={coverage.hasLocation}
                status={coverage.status}
                error={coverage.error}
                onRetry={coverage.refresh}
                onChangeLocation={() => setShowLocationPrompt(true)}
              />
              <MinimumOrderNotice
                shortfall={minimum.shortfall}
                minimum={minimum.minimum}
                subtotal={minimum.subtotal}
                vendorType="pharmacy"
              />
              <VendorOfferLine
                amount={cartOfferSavings(cartItems)}
                format={formatCurrency}
              />
              <CartSummary
                estimate={estimate}
                subtotal={goodsSubtotal}
                vendorId={currentVendor || vendorId}
                vendorType="pharmacy"
                style={{ '--cart-total-ink': '#C2185B' }}
              />
              <CartPromiseNote />
              {hasRestrictedItems && (
                <div className="restaurant-cart-savings">
                  <Typography className="restaurant-cart-savings-text">
                    <InfoOutlined className="restaurant-cart-savings-icon" />
                    Age verification may be required at checkout
                  </Typography>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Only the checkout button is pinned; everything above it scrolls
          with the items, so a short screen never hides the money. */}
      <div className="restaurant-cart-footer">
        <Button
          className="restaurant-cart-checkout-btn"
          onClick={checkoutHandler}
          disabled={!canProceed}
          variant="contained"
          fullWidth
          style={{
            backgroundColor: canProceed ? '#E91E63' : undefined,
            textTransform: 'none'
          }}
        >
          {minimumShortfall > 0
            ? minimumOrderMessage(minimumShortfall, formatCurrency)
            : coverage.isFailed
              ? "Delivery check failed — retry above"
              : coverage.isOutOfRange
                ? "Vendor doesn't deliver here"
                : !coverage.hasLocation
                  ? "Set your delivery location"
                  : !coverage.isConfirmed
                    ? "Checking delivery coverage…"
                    : "Proceed to Checkout"}
        </Button>
      </div>
    </div>
    {showLocationPrompt && (
      <LocationPrompt open onClose={() => setShowLocationPrompt(false)} />
    )}
    {/* Opening the cart checks it at its branch (4 Oct 2026). */}
    <BranchCartCheck vertical="healthcare" cartId={currentVendor} />
    </>
  );
};

export default HealthcareCart;

